import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { imageUrl } from "@/lib/api";
import { Link } from "react-router-dom";

export const LENS_COLORS = { visited: "#F27A54", lived: "#F59E0B", next: "#7C3AED" };
export const LENS_LABELS = { visited: "Visited", lived: "Lived", next: "Next" };

const PIN_COLOR = { photo: "#F27A54", video: "#EC4899" };

// ISO-3166 alpha-2 → flag emoji via regional indicator symbols.
function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => A + c.charCodeAt(0) - 65));
}

function bboxCenter(feature) {
  let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
  const walk = (coords) => {
    if (typeof coords[0] === "number") {
      const [lon, lat] = coords;
      if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
    } else coords.forEach(walk);
  };
  try { walk(feature.geometry.coordinates); } catch { return null; }
  return [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
}

function flagIcon(cc, count) {
  const w = count ? 60 : 46;
  return L.divIcon({
    className: "atlas-flag-icon",
    html: `<div data-testid="atlas-flag" style="display:flex;align-items:center;justify-content:center;gap:3px;width:100%;height:100%;white-space:nowrap;cursor:pointer;">
      <span style="font-size:26px;line-height:1;filter:drop-shadow(0 1px 2px rgba(0,0,0,.35));">${flagEmoji(cc)}</span>
      ${count ? `<span style="background:#2C2724;color:#fff;font:700 10px/1 system-ui;padding:2px 5px;border-radius:9px;">${count}</span>` : ""}
    </div>`,
    iconSize: [w, 44],
    iconAnchor: [w / 2, 22],
  });
}

function bubbleIcon(label, count, color) {
  return L.divIcon({
    className: "atlas-bubble-icon",
    html: `<div data-testid="atlas-bubble" style="transform:translate(-50%,-50%);white-space:nowrap;display:flex;align-items:center;gap:5px;background:#fff;border:2px solid ${color};border-radius:999px;padding:3px 9px;box-shadow:0 3px 10px rgba(44,39,36,.18);">
      <span style="width:8px;height:8px;border-radius:50%;background:${color};"></span>
      <span style="font:700 11px/1 system-ui;color:#2C2724;">${label}</span>
      <span style="font:700 10px/1 system-ui;color:#fff;background:${color};padding:2px 5px;border-radius:9px;">${count}</span>
    </div>`,
    iconSize: [0, 0],
  });
}

function pinIcon(lens, label) {
  const color = LENS_COLORS[lens] || "#F27A54";
  return L.divIcon({
    className: "atlas-manual-pin",
    html: `<div data-testid="manual-pin" style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center;">
      <span style="width:16px;height:16px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.3);"></span>
      ${label ? `<span style="margin-top:2px;background:#2C2724;color:#fff;font:700 9px/1 system-ui;padding:2px 5px;border-radius:6px;white-space:nowrap;">${label}</span>` : ""}
    </div>`,
    iconSize: [0, 0],
  });
}

function ClickCatcher({ onMapClick }) {
  useMapEvents({ click: (e) => { if (onMapClick) onMapClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function FitToData({ points }) {
  const map = useMap();
  useEffect(() => {
    const latlngs = points.map((p) => [p.lat, p.lon]);
    if (latlngs.length) {
      try { map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 6 }); } catch (e) { /* */ }
    }
  }, [points, map]);
  return null;
}

export default function AtlasView({ countries = {}, points = [], lensFilter = "all",
  granularity = "countries", editable = false, onCountryClick, photoBase = "/photos", height = "60vh",
  manualPins = [], pinMode = false, onMapClick, onDeletePin }) {
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    fetch("/world-countries.geojson").then((r) => r.json()).then(setGeo).catch(() => setGeo(null));
  }, []);

  const styleFn = useMemo(() => (feature) => {
    const a2 = feature.properties.iso_a2;
    const info = countries[a2];
    const show = info && info.lens && (lensFilter === "all" || info.lens === lensFilter);
    if (show) {
      return { fillColor: LENS_COLORS[info.lens] || "#F27A54", fillOpacity: 0.72, weight: 1, color: "#ffffff" };
    }
    return { fillColor: "#d4d4d8", fillOpacity: 0.12, weight: 0.4, color: "#cbd5e1" };
  }, [countries, lensFilter]);

  const geoKey = useMemo(() => JSON.stringify(Object.entries(countries).map(([k, v]) => [k, v.lens])) + lensFilter + pinMode, [countries, lensFilter, pinMode]);

  // Centroid per country from the GeoJSON, for placing flags.
  const centroids = useMemo(() => {
    const m = {};
    if (!geo) return m;
    geo.features.forEach((f) => { const c = bboxCenter(f); if (c) m[f.properties.iso_a2] = { c, name: f.properties.name }; });
    return m;
  }, [geo]);

  // Flags on every visited country matching the current lens filter.
  const flags = useMemo(() => {
    if (granularity !== "countries") return [];
    return Object.entries(countries)
      .filter(([a2, info]) => info.lens && (lensFilter === "all" || info.lens === lensFilter) && centroids[a2])
      .map(([a2, info]) => ({ a2, name: centroids[a2].name, center: centroids[a2].c, count: info.count || 0, info }));
  }, [countries, centroids, lensFilter, granularity]);

  // Cluster photo points by city or region (state) for the finer tabs.
  // Scope points to the active lens so Regions/Cities respect the lens filter.
  const scopedPoints = useMemo(() => {
    if (lensFilter === "all") return points;
    return points.filter((p) => {
      const info = countries[p.country_code];
      return info && info.lens === lensFilter;
    });
  }, [points, countries, lensFilter]);

  const clusters = useMemo(() => {
    if (granularity === "countries") return [];
    const keyField = granularity === "cities" ? "city" : "state";
    const groups = {};
    scopedPoints.forEach((p) => {
      const label = p[keyField] || (granularity === "regions" ? p.country : null);
      if (!label) return;
      const key = `${p.country || ""}|${label}`;
      if (!groups[key]) groups[key] = { label, country: p.country, sumLat: 0, sumLon: 0, n: 0, ids: [] };
      const g = groups[key];
      g.sumLat += p.lat; g.sumLon += p.lon; g.n += 1; if (g.ids.length < 4) g.ids.push(p.id);
    });
    return Object.values(groups).map((g) => ({ ...g, center: [g.sumLat / g.n, g.sumLon / g.n] }));
  }, [scopedPoints, granularity]);

  return (
    <div className={`overflow-hidden rounded-xl border border-border ${pinMode ? "atlas-pinmode" : ""}`} style={{ height, isolation: "isolate" }} data-testid="atlas-map">
      <MapContainer center={[20, 10]} zoom={2} scrollWheelZoom minZoom={2} worldCopyJump
        className="h-full w-full" style={{ background: "#EAF0EC" }}>
        <TileLayer attribution='&copy; OpenStreetMap, &copy; CARTO' url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
        {pinMode && <ClickCatcher onMapClick={onMapClick} />}        {geo && <GeoJSON key={geoKey} data={geo} style={styleFn}
          onEachFeature={(feature, layer) => {
            const a2 = feature.properties.iso_a2;
            const name = feature.properties.name;
            layer.on("click", () => onCountryClick?.(a2, name, countries[a2]));
            layer.bindTooltip(name, { sticky: true, direction: "top", opacity: 0.9 });
          }} />}
        <FitToData points={points} />

        {/* Countries: a flag on each visited country */}
        {flags.map((f) => (
          <Marker key={`flag-${f.a2}`} position={f.center} icon={flagIcon(f.a2, f.count)}
            eventHandlers={{ click: () => onCountryClick?.(f.a2, f.name, f.info) }}
            data-testid={`flag-${f.a2}`}>
            <Popup><span className="text-xs font-bold">{f.name}</span> · {f.count} item{f.count === 1 ? "" : "s"}</Popup>
          </Marker>
        ))}

        {/* Regions / Cities: labelled count bubbles */}
        {clusters.map((c, i) => (
          <Marker key={`cl-${i}`} position={c.center}
            icon={bubbleIcon(c.label, c.n, granularity === "cities" ? "#F27A54" : "#2D6A4F")}
            data-testid={`cluster-${granularity}-${i}`}>
            <Popup>
              <p className="text-xs font-bold">{c.label}{c.country ? `, ${c.country}` : ""}</p>
              <p className="mb-1 text-[11px] text-zinc-500">{c.n} item{c.n === 1 ? "" : "s"}</p>
              <div className="flex gap-1">
                {c.ids.map((id) => (
                  <Link key={id} to={`${photoBase}/${id}`}><img src={imageUrl(id, "display")} alt="" className="h-10 w-10 rounded object-cover" /></Link>
                ))}
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Individual photo dots only in the Cities view (finest granularity) */}
        {granularity === "cities" && scopedPoints.map((p) => (
          <CircleMarker key={p.id} center={[p.lat, p.lon]} radius={4}
            pathOptions={{ color: "#fff", weight: 1, fillColor: PIN_COLOR[p.media_type] || "#F27A54", fillOpacity: 0.85 }}>
            <Popup>
              <Link to={`${photoBase}/${p.id}`} className="block w-40">
                <div className="relative">
                  <img src={imageUrl(p.id, "display")} alt="" className="mb-1 h-24 w-full rounded object-cover" />
                  {p.media_type === "video" && <span className="absolute right-1 top-1 rounded-sm bg-[#EC4899] px-1 py-0.5 text-[8px] font-bold text-white">VIDEO</span>}
                </div>
                <p className="line-clamp-1 text-xs font-semibold">{p.title || "Untitled"}</p>
                <p className="text-[11px] text-zinc-500">{[p.city, p.country].filter(Boolean).join(", ")}</p>
              </Link>
            </Popup>
          </CircleMarker>
        ))}
        {/* Manual pins — places the user marked without photos */}
        {manualPins.map((p) => (
          <Marker key={`pin-${p.id}`} position={[p.lat, p.lon]} icon={pinIcon(p.lens, p.label)} data-testid={`manual-pin-${p.id}`}>
            <Popup>
              <p className="text-xs font-bold">{p.label}</p>
              <p className="text-[11px] capitalize text-zinc-500">{LENS_LABELS[p.lens] || p.lens}</p>
              {onDeletePin && (
                <button onClick={() => onDeletePin(p.id)} data-testid={`delete-pin-${p.id}`}
                  className="mt-1 rounded bg-red-50 px-2 py-1 text-[11px] font-bold text-red-600 hover:bg-red-100">Remove pin</button>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
